import { useMemo, useState, type ReactNode, type KeyboardEvent } from 'react';
import { Table, type TableProps } from '../Table/Table.js';
import { Pagination } from '../Pagination/Pagination.js';
import { EmptyState } from '../EmptyState/EmptyState.js';
import { Loader } from '../../core/Loader/Loader.js';
import './DataTable.css';

export interface DataTableColumn<R> {
  /** Unique column key. */
  accessor: keyof R | string;
  /** Display name in the header. */
  title?: ReactNode;
  /** Pure-functional cell renderer. */
  render?: (row: R, index: number) => ReactNode;
  width?: number | string;
  textAlign?: 'left' | 'center' | 'right';
  sortable?: boolean;
}

export interface DataTableProps<R> extends Omit<TableProps, 'children'> {
  records: R[];
  columns: DataTableColumn<R>[];
  /** Stable ID for each row (defaults to index). */
  idAccessor?: keyof R | ((row: R, i: number) => string | number);
  loading?: boolean;
  emptyState?: ReactNode;
  /** Optional pagination. */
  page?: number;
  totalRecords?: number;
  recordsPerPage?: number;
  onPageChange?: (page: number) => void;
  /** Optional click handler. */
  onRowClick?: (row: R, index: number) => void;
  sortStatus?: { columnAccessor: string; direction: 'asc' | 'desc' };
  onSortStatusChange?: (status: { columnAccessor: string; direction: 'asc' | 'desc' }) => void;
}

/**
 * Pragmatic data table: sortable headers, optional pagination, loading
 * overlay, empty state. For very large datasets pair the same column
 * shape with `@tanstack/react-table` (peer dep) inside a custom cell.
 */
export function DataTable<R extends Record<string, unknown>>(props: DataTableProps<R>): ReactNode {
  const {
    records, columns,
    idAccessor,
    loading, emptyState,
    page, totalRecords, recordsPerPage, onPageChange,
    onRowClick,
    sortStatus, onSortStatusChange,
    ...tableProps
  } = props;

  const [internalSort, setInternalSort] = useState<{ columnAccessor: string; direction: 'asc' | 'desc' } | null>(null);
  const sort = sortStatus ?? internalSort;

  const sortedRecords = useMemo(() => {
    if (!sort) return records;
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...records].sort((a, b) => {
      const av = (a as Record<string, unknown>)[sort.columnAccessor];
      const bv = (b as Record<string, unknown>)[sort.columnAccessor];
      if (av == null) return 1; if (bv == null) return -1;
      if (av < bv) return -1 * dir; if (av > bv) return 1 * dir;
      return 0;
    });
  }, [records, sort]);

  const setSort = (col: string) => {
    const next: { columnAccessor: string; direction: 'asc' | 'desc' } =
      sort?.columnAccessor === col
        ? { columnAccessor: col, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
        : { columnAccessor: col, direction: 'asc' };
    if (onSortStatusChange) onSortStatusChange(next);
    else setInternalSort(next);
  };

  const idFor = (row: R, i: number): string | number => {
    if (typeof idAccessor === 'function') return idAccessor(row, i);
    if (idAccessor && row[idAccessor] != null) return row[idAccessor] as string | number;
    return i;
  };

  return (
    <div className="rr-data-table">
      <div className="rr-data-table__scroll">
        <Table {...tableProps}>
          <Table.Thead>
            <Table.Tr>
              {columns.map((col) => {
                const key = String(col.accessor);
                const isSorted = sort?.columnAccessor === key;
                return (
                  <Table.Th
                    key={key}
                    style={{ width: col.width, textAlign: col.textAlign }}
                    aria-sort={isSorted ? (sort?.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {col.sortable ? (
                      <button type="button" className="rr-data-table__sort" onClick={() => setSort(key)}>
                        {col.title ?? key}
                        <span className="rr-data-table__sort-icon" aria-hidden>
                          {isSorted ? (sort?.direction === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    ) : (col.title ?? key)}
                  </Table.Th>
                );
              })}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedRecords.map((row, i) => (
              <Table.Tr
                key={idFor(row, i)}
                style={{ cursor: onRowClick ? 'pointer' : undefined }}
                tabIndex={onRowClick ? 0 : undefined}
                role={onRowClick ? 'button' : undefined}
                onKeyDown={onRowClick ? (e: KeyboardEvent<HTMLTableRowElement>) => {
                  // Enter and Space are what a button answers to, and the row
                  // is acting as one. Space also scrolls the page, so it is
                  // suppressed here.
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRowClick(row, i);
                  }
                } : undefined}
                onClick={onRowClick ? () => onRowClick(row, i) : undefined}
              >
                {columns.map((col) => {
                  const key = String(col.accessor);
                  const value = col.render ? col.render(row, i) : (row as Record<string, unknown>)[key] as ReactNode;
                  return (
                    <Table.Td key={key} style={{ textAlign: col.textAlign }}>{value}</Table.Td>
                  );
                })}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {loading ? (
          <div className="rr-data-table__loading" aria-live="polite"><Loader /></div>
        ) : null}
        {!loading && records.length === 0 ? (
          <div className="rr-data-table__empty">
            {emptyState ?? <EmptyState title="No records" description="Nothing matches your filters yet." />}
          </div>
        ) : null}
      </div>
      {page !== undefined && totalRecords !== undefined && recordsPerPage ? (
        <div className="rr-data-table__pagination">
          <Pagination
            value={page}
            total={Math.max(1, Math.ceil(totalRecords / recordsPerPage))}
            onChange={onPageChange}
          />
        </div>
      ) : null}
    </div>
  );
}
